// Shared by the dashboard and standalone widget. Dynamic values are escaped, not trusted HTML.
const marker = '{#languageprogramlistregion}';
export const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[c]));
export function safeHttpUrl(value) {
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : null; } catch { return null; }
}
export function formatMoney(value, currency) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
  if (!/^[A-Z]{3}$/.test(currency || '')) return null;
  try { return new Intl.NumberFormat(undefined, {style:'currency', currency, currencyDisplay:'code'}).format(Number(value)); } catch { return null; }
}
const detail = (label, value) => value === null || value === undefined || value === '' ? ''
  : `<div class="program-detail"><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</div>`;
const money = (label, value, currency) => detail(label, formatMoney(value, currency));
const warning = value => `<div class="program-detail language-quote-warning"><strong>${escapeHtml(value)}</strong></div>`;

export function renderLanguageProgramCard(card) {
  const p = card?.program || {};
  const q = card?.quote || {};
  const currency = q.currency;
  const status = {
    needs_dates: !q.requestedStartDate && !q.requestedWeeks ? 'Provide a start date and duration to check prices.'
      : !q.requestedStartDate ? 'Provide a start date to check prices.' : 'Provide a duration in weeks to check prices.',
    invalid_request: 'A future start date and a valid duration are required.',
    missing: 'No price found for the requested date and duration.',
    reference_season: 'Reference season price only. Not a quote for the requested start date.',
    alternative_duration: 'This price is for a different duration from the one requested.',
    unavailable: 'Current prices could not be checked.'
  }[q.status];
  const website = safeHttpUrl(p.websiteUrl);
  // Relative source filenames have no inferred public location. Use an initials placeholder instead.
  const logo = safeHttpUrl(p.schoolLogo);
  const initials = String(p.schoolName || 'School').split(/\s+/).filter(Boolean).slice(0,2).map(x=>x[0]).join('').toUpperCase();
  const supplements = Array.isArray(q.supplements) ? q.supplements : [];
  const knownDates = Array.isArray(q.knownStartDates) ? q.knownStartDates : [];
  return `<div class="program-code-block language-program-card">
    <div class="program-link program-logo">
      ${logo ? `<img src="${escapeHtml(logo)}" alt="${escapeHtml(p.schoolName || 'School')}" loading="lazy" class="program-logo-image">` : `<div class="program-logo-placeholder">${escapeHtml(initials)}</div>`}
      <div class="program-logo-number">${escapeHtml(p.programSubtypeName || p.programTypeName || 'Language program')}</div>
      <div class="program-logo-name"><strong class="program-school-name">${escapeHtml(p.schoolName)}</strong>
        <span class="program-location">${escapeHtml([p.cityName,p.stateName,p.countryName].filter(Boolean).join(', '))}</span></div>
    </div>
    <div class="program-name">${escapeHtml(p.programName)}</div>
    ${detail('Lessons per week', p.lessonsPerWeek)}
    ${detail('Delivery', p.isOnline ? 'Online' : 'On campus')}
    ${detail('Requested start', q.requestedStartDate)}
    ${detail('Requested weeks', q.requestedWeeks)}
    ${status ? warning(status) : ''}
    ${q.status === 'reference_season' ? detail('Reference season', [q.seasonStart,q.seasonEnd].filter(Boolean).join(' – ')) : ''}
    ${detail('Price is for', q.priceWeeks ? `${q.priceWeeks} weeks` : null)}
    ${money('Tuition', q.tuition, currency)}
    ${q.promotionName ? detail('Promotion', q.promotionName) : ''}
    ${money('Tuition after promotion', q.discountedTuition, currency)}
    ${money('Registration fee', q.enrollmentFee, currency)}
    ${money('Materials', q.materialFee, currency)}
    ${supplements.map(x=>money(`${x.name} (${x.weeks} weeks)`,x.amount,currency)).join('')}
    ${money('Course subtotal', q.courseSubtotal, currency)}
    ${q.warnings?.includes('fees_unconfirmed') ? warning('Additional course fees could not be fully confirmed.') : ''}
    ${q.startDateStatus === 'not_listed' ? warning('The requested start date is not listed in the source schedule.') : ''}
    ${q.startDateStatus === 'alternate' ? warning('This is listed as an alternate start date. School confirmation required.') : ''}
    ${q.startDateStatus === 'unconfirmed' ? detail('Start date', 'School confirmation required.') : ''}
    ${knownDates.length ? detail('Listed upcoming starts',knownDates.join(', ')) : ''}
    ${q.checkedAtUtc ? detail('Checked', new Date(q.checkedAtUtc).toLocaleString()) : ''}
    <div class="program-detail">Course prices do not include accommodation, travel or insurance. Start dates and places require confirmation.</div>
    ${website ? `<a class="program-link-btn" href="${escapeHtml(website)}" target="_blank" rel="noopener noreferrer">School website ↗</a>` : ''}
  </div>`;
}

export function renderLanguageProgramMessage(text, cards) {
  const [before = '', ...after] = String(text || '').split(marker);
  const block = value => value.trim() ? `<div class="program-message-text ea-program-message-text" style="white-space:pre-wrap">${escapeHtml(value.trim())}</div>` : '';
  return block(before) + `<div class="program-card-list ea-program-list">${cards.map(renderLanguageProgramCard).join('')}</div>` + block(after.join(''));
}
